import React, { useCallback, useEffect, useRef, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import StopCircleIcon from "@mui/icons-material/StopCircle";

interface VoiceMicProps {
  fieldName: string;
  baseValue: string;
  disabled?: boolean;
  onLiveChange: (value: string) => void;
  onFinalize: (value: string) => void;
}

// Consolidate 'window as any' access
const getSpeechRecognition = (): any | null => {
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
};

// Helper to safely stop recognition
const safeStop = (recognition: any | null) => {
  if (recognition && typeof recognition.stop === "function") {
    try { recognition.stop(); } catch (_) {}
  }
};

const FormFieldVoiceMic: React.FC<VoiceMicProps> = ({
  // fieldName is unused, can be removed from destructuring if not needed for future features
  baseValue,
  disabled,
  onLiveChange,
  onFinalize,
}) => {
  const recognitionRef = useRef<any | null>(null);
  const [listening, setListening] = useState(false);
  // Initial state is baseValue || "" for all refs
  const baseRef = useRef<string>(baseValue || "");
  const latestCombinedRef = useRef<string>(baseValue || "");
  const activeRef = useRef<boolean>(false);

  // Sync external baseValue only when not actively listening
  useEffect(() => {
    if (!activeRef.current) {
      const value = baseValue || "";
      baseRef.current = value;
      latestCombinedRef.current = value;
    }
  }, [baseValue]);

  const start = useCallback(() => {
    const SR = getSpeechRecognition();
    if (!SR || disabled || recognitionRef.current) return; // Exit early if unsupported, disabled, or already running

    const recognition = new SR();
    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.continuous = false; 

    recognitionRef.current = recognition;
    setListening(true);
    activeRef.current = true;

    let finalTranscript = "";

    recognition.onstart = () => {
      finalTranscript = "";
      // Snapshot current external value if not already active (a safe check for restarts)
      if (!activeRef.current) {
        baseRef.current = baseValue || baseRef.current || "";
      }
      latestCombinedRef.current = baseRef.current;
      onLiveChange(baseRef.current);
    };

    recognition.onresult = (event: any) => {
      let interimTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalTranscript += transcript;
        else interimTranscript += transcript;
      }
      
      const preferred = finalTranscript || interimTranscript;
      const base = baseRef.current;
      
      // Simplify string concatenation logic
      const combined = base + (base && preferred ? " " : "") + preferred; 
      
      latestCombinedRef.current = combined.trim();
      onLiveChange(latestCombinedRef.current);
    };

    const cleanup = (shouldFinalize = true) => {
      setListening(false);
      recognitionRef.current = null;
      activeRef.current = false;
      if (shouldFinalize) {
        onFinalize((latestCombinedRef.current || baseRef.current).trim());
      }
    };

    recognition.onend = () => {
      const finalCombined = (latestCombinedRef.current || baseRef.current).trim();
      baseRef.current = finalCombined; // persist as new base
      
      if (activeRef.current) {
        try {
          finalTranscript = ""; // reset accumulator
          recognition.start(); // restart
          return;
        } catch (_) {}
      }
      
      cleanup(false); // don't finalize here, let stop or error handle it
      onFinalize(finalCombined); // Finalize after successful end/restart failure
    };

    // Consolidate error and cleanup logic
    recognition.onerror = () => cleanup(); 

    try {
      recognition.start();
    } catch (_) {
      cleanup();
    }
  }, [disabled, onLiveChange, onFinalize, baseValue]); // Added baseValue dependency to prevent stale value in start

  const stop = useCallback(() => {
    activeRef.current = false; // Flag to prevent auto-restart in onend
    safeStop(recognitionRef.current); // Use the helper
    recognitionRef.current = null;
    setListening(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      activeRef.current = false;
      safeStop(recognitionRef.current);
      recognitionRef.current = null;
    };
  }, []);

  const supported = Boolean(getSpeechRecognition());
  const isDisabled = disabled || !supported;

  // Use a single return statement with a ternary for the button logic
  const icon = listening ? <StopCircleIcon color="error" /> : <MicIcon />;
  const label = listening ? "stop voice input" : "start voice input";
  const title = isDisabled ? "Voice not available" : (listening ? "Stop" : "Speak");
  const handler = listening ? stop : start;

  return (
    <Tooltip title={title}>
      <span>
        <IconButton aria-label={label} onClick={handler} edge="end" size="small" disabled={isDisabled}>
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default FormFieldVoiceMic;